/* Operazioni pure sul documento: ognuna ritorna un NUOVO DiagramDoc (clone),
   il documento in ingresso non viene mai mutato. La validazione (Zod +
   riferimenti + blocchi) è responsabilità del chiamante prima del salvataggio. */
import type { DiagramDoc, Doc, FlowStep, Participant } from "@fullsteps/shared";

export function cloneDoc(doc: DiagramDoc): DiagramDoc {
  return structuredClone(doc);
}

/** Indice dell'`end` che chiude il `block` in posizione i. -1 se non trovato. */
export function matchingEnd(flow: FlowStep[], i: number): number {
  let depth = 0;
  for (let j = i; j < flow.length; j++) {
    const t = flow[j]!.t;
    if (t === "block") depth++;
    else if (t === "end") {
      depth--;
      if (depth === 0) return j;
    }
  }
  return -1;
}

export function insertStep(doc: DiagramDoc, index: number, step: FlowStep): DiagramDoc {
  const d = cloneDoc(doc);
  d.flow.splice(index, 0, step);
  return d;
}

/** Inserisce un blocco completo (block + [else] + end) come gruppo. */
export function insertBlock(doc: DiagramDoc, index: number, kind: "alt" | "opt" | "loop"): DiagramDoc {
  const d = cloneDoc(doc);
  const group: FlowStep[] =
    kind === "alt"
      ? [
          { t: "block", kind, cond: "condizione" },
          { t: "else", cond: "altrimenti" },
          { t: "end" },
        ]
      : [{ t: "block", kind, cond: "condizione" }, { t: "end" }];
  d.flow.splice(index, 0, ...group);
  return d;
}

export function updateStep(doc: DiagramDoc, index: number, patch: Partial<FlowStep>): DiagramDoc {
  const d = cloneDoc(doc);
  d.flow[index] = { ...d.flow[index], ...patch } as FlowStep;
  return d;
}

/** Elimina uno step. Un `block` elimina l'intero gruppo fino al suo `end`.
    `else`/`end` non sono eliminabili singolarmente (ritorna il doc invariato). */
export function deleteStep(doc: DiagramDoc, index: number): DiagramDoc {
  const d = cloneDoc(doc);
  const s = d.flow[index]!;
  if (s.t === "block") {
    const e = matchingEnd(d.flow, index);
    d.flow.splice(index, e === -1 ? 1 : e - index + 1);
  } else if (s.t === "else" || s.t === "end") {
    return doc;
  } else {
    d.flow.splice(index, 1);
  }
  return d;
}

const isLeaf = (t: FlowStep["t"]): boolean => t !== "block" && t !== "else" && t !== "end";

/** Scambia con il vicino (solo step "foglia", non i confini di blocco). */
export function moveStep(doc: DiagramDoc, index: number, dir: -1 | 1): DiagramDoc {
  const j = index + dir;
  if (j < 0 || j >= doc.flow.length) return doc;
  const a = doc.flow[index]!;
  const b = doc.flow[j]!;
  if (!isLeaf(a.t) || !isLeaf(b.t)) return doc;
  const d = cloneDoc(doc);
  d.flow[index] = b;
  d.flow[j] = a;
  return d;
}

export function canMove(doc: DiagramDoc, index: number, dir: -1 | 1): boolean {
  const j = index + dir;
  if (j < 0 || j >= doc.flow.length) return false;
  return isLeaf(doc.flow[index]!.t) && isLeaf(doc.flow[j]!.t);
}

/* ---- partecipanti ---- */

export function addParticipant(doc: DiagramDoc, p: Participant): DiagramDoc {
  const d = cloneDoc(doc);
  d.participants.push(p);
  return d;
}
export function updateParticipant(doc: DiagramDoc, id: string, patch: Partial<Participant>): DiagramDoc {
  const d = cloneDoc(doc);
  const i = d.participants.findIndex((p) => p.id === id);
  if (i >= 0) d.participants[i] = { ...d.participants[i], ...patch } as Participant;
  return d;
}
export function removeParticipant(doc: DiagramDoc, id: string): DiagramDoc {
  const d = cloneDoc(doc);
  d.participants = d.participants.filter((p) => p.id !== id);
  return d;
}
/** Conta i riferimenti a un partecipante nel flow (per bloccare rimozioni). */
export function participantRefs(doc: DiagramDoc, id: string): number {
  let n = 0;
  for (const s of doc.flow) {
    if ((s.t === "msg" && (s.from === id || s.to === id)) ||
      (s.t === "note" && s.over === id) ||
      ((s.t === "act" || s.t === "deact" || s.t === "create" || s.t === "destroy") && s.a === id))
      n++;
  }
  return n;
}

/* ---- documenti ---- */

export function addDoc(doc: DiagramDoc, entry: Doc): DiagramDoc {
  const d = cloneDoc(doc);
  d.docs.push(entry);
  return d;
}
export function updateDoc(doc: DiagramDoc, id: string, patch: Partial<Doc>): DiagramDoc {
  const d = cloneDoc(doc);
  const i = d.docs.findIndex((x) => x.id === id);
  if (i >= 0) d.docs[i] = { ...d.docs[i], ...patch } as Doc;
  return d;
}
export function removeDoc(doc: DiagramDoc, id: string): DiagramDoc {
  const d = cloneDoc(doc);
  d.docs = d.docs.filter((x) => x.id !== id);
  for (const s of d.flow) if (s.t === "msg" && s.docs) s.docs = s.docs.filter((x) => x !== id);
  return d;
}
export function docRefs(doc: DiagramDoc, id: string): number {
  let n = 0;
  for (const s of doc.flow) if (s.t === "msg" && s.docs?.includes(id)) n++;
  return n;
}

/** id "slug" univoco derivato da un testo. */
export function slugify(text: string, taken: Set<string>): string {
  const base = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 20) || "id";
  let id = base;
  let i = 2;
  while (taken.has(id)) id = `${base}-${i++}`;
  return id;
}
